import axios, { AxiosInstance } from 'axios';
import { apiUrl, endpoints } from './endpoints';
import {
  AccountOpeningMetadataRequest,
  AccountOpeningMetadataResponse,
  AccountOpeningRequest,
  AccountOpeningResponse,
  AddAcknowledgementsRequest,
  AddDocumentUploadRequest,
  AddDisbursementAccountRequest,
  AddFeesRequest,
  AddInvestmentProfileRequest,
  AddPersonalDetailsRequest,
  GetExistingAccountOpeningFormsRequest,
  DeleteExistingAccountOpeningFormsRequest,
  SaveTentativeAccountRequest,
  StartAccountOpeningRequest,
  UpdateInvestmentProfileRequest,
  UpdatePersonalDetailsRequest,
} from './types';
import {
  readSessionToken,
  writeSessionToken,
} from '@/utils/session-token';

class APIClient {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: apiUrl,
      headers: {
        authorization: this.getBearerToken()
          ? `bearer ${this.getBearerToken()}`
          : undefined,
      },
    });
  }

  public setBearerToken(value: string | null): void {
    if (value) {
      writeSessionToken(value);
      this.client.defaults.headers.authorization = `bearer ${value}`;
    } else {
      writeSessionToken(null);
      this.client.defaults.headers.authorization = undefined;
    }
  }

  public getBearerToken(): string | null {
    return sessionStorage.getItem('clientToken') === null
      ? readSessionToken()
      : sessionStorage.getItem('clientToken');
  }

  public async getAccountOpening(
    request: AccountOpeningRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.get<readonly AccountOpeningResponse[]>(
      'accounts/opening',
      { params: request },
    );

    return data[0];
  }

  public async startAccountOpening(
    request: StartAccountOpeningRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post('accounts/opening/start', request);
    return data;
  }
  
  public async getClients() {
    const { data } = await this.client.get(endpoints.dueDiligenceMetadata);
  }

  public async getAccountOpeningMetadata(
    request: AccountOpeningMetadataRequest,
  ): Promise<AccountOpeningMetadataResponse> {
    const { data } = await this.client.get('accounts/opening/form', {
      params: request,
    });
    return data;
  }

  public async getExistingAccountOpeningForms(
    request: GetExistingAccountOpeningFormsRequest,
  ): Promise<readonly AccountOpeningResponse[]> {
    const { data } = await this.client.get('accounts/opening', {
      params: request,
    });
    return data;
  }

  public async deleteAccount(
    request: DeleteExistingAccountOpeningFormsRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/delete?accountOpeningId=' + request,
    );
    return data;
  }

  public async addAccountType(
    request: SaveTentativeAccountRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/accounttype/add',
      request,
    );
    return data;
  }

  public async updateAccountType(
    id: number,
    request: SaveTentativeAccountRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/accounttype/update',
      {
        ID: id,
        ...request,
      },
    );
    return data;
  }

  public async addPersonalDetails(
    request: AddPersonalDetailsRequest[],
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/personaldetails/add',
      request,
    );
    return data;
  }

  public async updatePersonalDetails(
    request: UpdatePersonalDetailsRequest[],
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/personaldetails/update',
      request,
    );
    return data;
  }

  public async addDisbursementAccount(
    request: AddDisbursementAccountRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/disbursement/add',
      request,
    );
    return data;
  }

  public async removeDisbursementAccount(
    id: number,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/disbursement/delete',
      {
        ID: id,
      },
    );
    return data;
  }

  public async addInvestmentProfile(
    request: AddInvestmentProfileRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/investmentprofile/add',
      request,
    );
    return data;
  }

  public async updateInvestmentProfile(
    id: number,
    request: UpdateInvestmentProfileRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/investmentprofile/update',
      {
        ID: id,
        ...request,
      },
    );

    return data;
  }

  public async getDefaultFees(
    request: AccountOpeningMetadataRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.get('accounts/opening/feesinfo', {
      params: request,
    });

    return data;
  }

  public async addFees(
    request: AddFeesRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/fees/add',
      request,
    );
    return data;
  }

  public async updateFees(
    id: number,
    request: AddFeesRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post('accounts/opening/fees/update', {
      ID: id,
      ...request,
    });
    return data;
  }

  public async addAcknowledgements(
    request: AddAcknowledgementsRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/acknowldedgements/add',
      request,
    );
    return data;
  }

  public async updateAcknowledgements(
    id: number,
    request: AddAcknowledgementsRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/acknowldedgements/update',
      {
        ID: id,
        ...request,
      },
    );
    return data;
  }
  public async uploadDocuments(
    request: AddDocumentUploadRequest,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/document/add' +
        '?accountOpeningId=' +
        request.accountOpeningId +
        '&documentTypeId=' +
        request.documentTypeId,
      request.file,
    );
    return data;
  }
  public async deleteDocuments(request: any): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/document/delete' + '?documentId=' + request.documentId,
      request.file,
    );
    return data;
  }
  public async generateAccountOpeningPDF(
    request: any,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post('accounts/opening/sign', request);
    return data;
  }
  public async completeAccountOpeningForm(
    request: any,
  ): Promise<AccountOpeningResponse> {
    const { data } = await this.client.post(
      'accounts/opening/complete' +
        '?accountOpeningId=' +
        request.accountOpeningId,
    );
    return data;
  }
}

export default new APIClient();
